import { useParams, Navigate } from "react-router-dom";
import { Link } from "@/i18n/routing";
import {
  ArrowUpRight, MessageCircle, Check, MapPin, HeartPulse, Info,
  MessagesSquare, ClipboardCheck, Search, CalendarCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { LeadDialog } from "@/components/LeadDialog";
import { ContactChannels } from "@/components/ContactChannels";
import { SafetyStrip } from "@/components/SafetyStrip";
import { EmergencyNotice } from "@/components/EmergencyNotice";
import { URGENT_SERVICES, NURSING_SERVICES, LOCATIONS, CONTACT, waLink } from "@/data/site";
import { getServiceContent } from "@/data/serviceContent";

const BASE = "https://doctorsvietnam.com";

const CONFIG = {
  urgent: { base: "/urgent-care", label: "Urgent Care", data: URGENT_SERVICES, type: "medical" },
  nursing: { base: "/nursing", label: "Nursing & Home Care", data: NURSING_SERVICES, type: "nursing" },
};

const STEP_ICONS = [MessagesSquare, ClipboardCheck, Search, CalendarCheck];

const ServiceDetail = ({ kind }) => {
  const { slug, lang } = useParams();
  const cfg = CONFIG[kind];
  const service = cfg.data.find((s) => s.slug === slug);
  if (!service) return <Navigate to={cfg.base} replace />;

  const content = getServiceContent(slug);
  const Icon = service.icon;
  const others = cfg.data.filter((s) => s.slug !== slug).slice(0, 4);

  // Enriched content available for these six slugs; else fall back to short template.
  const enriched = !!content;
  const title = enriched ? content.h1 : service.title;
  const description = enriched ? content.metaDescription : service.intro;
  const seoTitle = enriched ? content.seoTitle : service.title;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/${lang}` },
      { "@type": "ListItem", position: 2, name: cfg.label, item: `${BASE}/${lang}${cfg.base}` },
      { "@type": "ListItem", position: 3, name: title, item: `${BASE}/${lang}${cfg.base}/${service.slug}` },
    ],
  };

  return (
    <>
      <Seo
        exact
        title={seoTitle}
        description={description}
        path={`${cfg.base}/${service.slug}`}
        jsonLd={[breadcrumbSchema]}
      />
      <PageHero
        eyebrow={cfg.label}
        title={title}
        subtitle={enriched ? content.opening : service.short}
        crumbs={[
          { label: "Home", to: "/" },
          { label: cfg.label, to: cfg.base },
          { label: title },
        ]}
      >
        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
          <LeadDialog
            title={title}
            defaultType={cfg.type}
            presetService={title}
            source={`service-${service.slug}`}
            trigger={
              <Button className="bg-brand py-5 font-bold text-white hover:opacity-90" data-testid="hero-get-support-btn">
                Get Medical Support
              </Button>
            }
          />
          <Button asChild variant="outline" className="border-2 border-brand/25 bg-white py-5 font-bold text-brand hover:bg-mint" data-testid="hero-whatsapp-btn">
            <a href={waLink(`Hello Doctors Vietnam, I need help with ${title}.`)} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us
            </a>
          </Button>
        </div>
      </PageHero>

      <section className="vc-container py-14 sm:py-16">
        <Reveal><EmergencyNotice className="mb-8" /></Reveal>

        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-12">
            {/* Icon + opening (short) */}
            <Reveal>
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-mint text-brand">
                <Icon className="h-7 w-7" />
              </span>
              <p className="mt-5 text-base leading-relaxed text-[hsl(var(--vc-ink))]">
                {enriched ? content.opening : service.intro}
              </p>
            </Reveal>

            {/* 1. Quick Answer */}
            {enriched && (
              <Reveal>
                <h2 className="text-2xl font-extrabold text-[hsl(var(--vc-ink))]">{content.quickAnswer.title}</h2>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {content.quickAnswer.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft">
                      <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-mint text-brand"><Check className="h-3.5 w-3.5" /></span>
                      <span className="text-sm text-[hsl(var(--vc-ink))]">{b}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}

            {/* 2. How It Works */}
            {enriched && (
              <Reveal>
                <h2 className="text-2xl font-extrabold text-[hsl(var(--vc-ink))]">How it works</h2>
                <ol className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {content.howItWorks.map((s, i) => {
                    const SIcon = STEP_ICONS[i] || MessagesSquare;
                    return (
                      <li key={s.title} className="rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-5 vc-shadow-soft">
                        <div className="flex items-center gap-2">
                          <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand text-white"><SIcon className="h-4 w-4" /></span>
                          <span className="text-sm font-bold text-brand">{String(i + 1).padStart(2, "0")}</span>
                        </div>
                        <p className="mt-3 text-sm font-bold text-[hsl(var(--vc-ink))]">{s.title}</p>
                        <p className="mt-1 text-xs leading-relaxed text-[hsl(var(--vc-slate))]">{s.text}</p>
                      </li>
                    );
                  })}
                </ol>
              </Reveal>
            )}

            {/* 3. What we can help coordinate */}
            {enriched && content.coordinate && (
              <Reveal>
                <h2 className="text-2xl font-extrabold text-[hsl(var(--vc-ink))]">{content.coordinate.title}</h2>
                {content.coordinate.note && (
                  <p className="mt-2 text-sm text-[hsl(var(--vc-slate))]">{content.coordinate.note}</p>
                )}
                {content.coordinate.grid ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {content.coordinate.grid.map((g) => (
                      <div key={g} className="flex items-center gap-2 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-3 vc-shadow-soft">
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-mint text-brand"><HeartPulse className="h-3.5 w-3.5" /></span>
                        <span className="text-sm font-semibold text-[hsl(var(--vc-ink))]">{g}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                    {(content.coordinate.items || []).map((it) => (
                      <li key={it} className="flex items-start gap-3 text-sm text-[hsl(var(--vc-ink))]">
                        <span className="mt-1 grid h-4 w-4 shrink-0 place-items-center rounded-full bg-brand text-white"><Check className="h-2.5 w-2.5" /></span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Reveal>
            )}

            {/* 4. Common Reasons chips */}
            {enriched && content.commonReasons && (
              <Reveal>
                <h2 className="text-2xl font-extrabold text-[hsl(var(--vc-ink))]">{content.commonReasons.title}</h2>
                {content.commonReasons.note && (
                  <p className="mt-2 text-sm text-[hsl(var(--vc-slate))]">{content.commonReasons.note}</p>
                )}
                <div className="mt-5 flex flex-wrap gap-2">
                  {content.commonReasons.chips.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--vc-line))] bg-mint px-3.5 py-1.5 text-sm font-medium text-[hsl(var(--vc-ink))]">
                      {c}
                    </span>
                  ))}
                </div>
              </Reveal>
            )}

            {/* 5. What to Expect (two-col) */}
            {enriched && content.whatToExpect && (
              <Reveal>
                <h2 className="text-2xl font-extrabold text-[hsl(var(--vc-ink))]">What to expect</h2>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {content.whatToExpect.map((e) => (
                    <div key={e.q} className="rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-5 vc-shadow-soft">
                      <p className="text-sm font-bold text-[hsl(var(--vc-ink))]">{e.q}</p>
                      <p className="mt-2 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{e.a}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            {/* 6. Where we can help */}
            <Reveal>
              <h2 className="text-2xl font-extrabold text-[hsl(var(--vc-ink))]">Where we can help</h2>
              <p className="mt-2 text-sm text-[hsl(var(--vc-slate))]">Availability varies by location, provider and time.</p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {LOCATIONS.map((l) => (
                  <Link
                    key={l.slug}
                    to={`/locations/${l.slug}`}
                    data-testid={`service-location-${l.slug}`}
                    className="group flex items-center justify-between gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-3 vc-shadow-soft transition hover:border-brand/40 hover:vc-shadow-card"
                  >
                    <span className="flex items-center gap-2">
                      <span className="grid h-7 w-7 place-items-center rounded-lg bg-mint text-brand"><MapPin className="h-3.5 w-3.5" /></span>
                      <span className="text-sm font-semibold text-[hsl(var(--vc-ink))]">
                        {kind === "nursing" ? `Nursing in ${l.name}` : `Doctor in ${l.name}`}
                      </span>
                    </span>
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-brand transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                ))}
              </div>
            </Reveal>

            {/* 7. Safety strip (compact) */}
            <Reveal><SafetyStrip /></Reveal>

            {/* 8. FAQ accordion */}
            {enriched && content.faqs && (
              <Reveal>
                <h2 className="text-2xl font-extrabold text-[hsl(var(--vc-ink))]">Frequently asked questions</h2>
                <Accordion type="single" collapsible className="mt-5" data-testid="service-faq-accordion">
                  {content.faqs.map((f, i) => (
                    <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-[hsl(var(--vc-line))]">
                      <AccordionTrigger className="text-left text-base font-bold text-[hsl(var(--vc-ink))] hover:no-underline" data-testid={`service-faq-trigger-${i}`}>
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed text-[hsl(var(--vc-slate))]">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            )}

            {/* Contextual VietClinics mentions kept for these two slugs only */}
            {service.slug === "post-discharge-support" && (
              <Reveal>
                <p className="rounded-xl border border-[hsl(var(--vc-line))] bg-mint p-4 text-sm leading-relaxed text-[hsl(var(--vc-slate))]" data-testid="vietclinics-context-link">
                  Planning surgery, dental treatment or another medical procedure in Vietnam? Explore wider treatment and{" "}
                  <a href="https://www.vietclinics.com/medical-tourism" target="_blank" rel="noopener" className="font-semibold text-brand underline underline-offset-2">
                    international patient coordination through VietClinics
                  </a>.
                </p>
              </Reveal>
            )}
            {service.slug === "clinic-booking" && (
              <Reveal>
                <p className="rounded-xl border border-[hsl(var(--vc-line))] bg-mint p-4 text-sm leading-relaxed text-[hsl(var(--vc-slate))]" data-testid="vietclinics-context-link">
                  For planned dental, cosmetic or specialist treatment, visit VietClinics for{" "}
                  <a href="https://www.vietclinics.com/" target="_blank" rel="noopener" className="font-semibold text-brand underline underline-offset-2">
                    broader medical-treatment coordination
                  </a>.
                </p>
              </Reveal>
            )}
          </div>

          {/* Sticky sidebar — placeholder image REMOVED */}
          <aside>
            <div className="lg:sticky lg:top-24 rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-card">
              <h3 className="text-lg font-extrabold text-[hsl(var(--vc-ink))]">Request this support</h3>
              <p className="mt-1.5 text-sm text-[hsl(var(--vc-slate))]">Tell us what you need — availability is confirmed individually.</p>
              <LeadDialog
                title={title}
                defaultType={cfg.type}
                presetService={title}
                source={`service-${service.slug}-sidebar`}
                trigger={
                  <Button className="mt-4 w-full bg-brand py-5 font-bold text-white hover:opacity-90" data-testid="service-request-btn">
                    Get Medical Support
                  </Button>
                }
              />
              <Button asChild variant="outline" className="mt-3 w-full border-2 border-[hsl(var(--vc-line))] font-bold text-[hsl(var(--vc-ink))] hover:border-brand hover:bg-mint hover:text-brand">
                <a href={waLink(`Hello Doctors Vietnam, I'm interested in ${title}.`)} target="_blank" rel="noopener noreferrer" data-testid="service-whatsapp-btn">
                  <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp us
                </a>
              </Button>

              <div className="mt-6 space-y-2 border-t border-[hsl(var(--vc-line))] pt-5 text-sm">
                <Link to="/common-health-problems-vietnam" className="flex items-center gap-2 text-[hsl(var(--vc-ink))] hover:text-brand" data-testid="sidebar-common-health-link">
                  <Info className="h-4 w-4 text-brand" /> Common Health Problems in Vietnam
                </Link>
                <Link to="/contact" className="flex items-center gap-2 text-[hsl(var(--vc-ink))] hover:text-brand" data-testid="sidebar-contact-link">
                  <MessagesSquare className="h-4 w-4 text-brand" /> Contact Doctors Vietnam
                </Link>
              </div>

              <ContactChannels variant="inline" className="mt-5 flex-col gap-3" />
              <p className="mt-4 text-xs text-[hsl(var(--vc-slate))]">
                {CONTACT.whatsappDisplay} · {CONTACT.email}
              </p>
            </div>
          </aside>
        </div>

        {/* Related services */}
        <div className="mt-16">
          <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">Other {cfg.label.toLowerCase()} services</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((o) => (
              <Link key={o.slug} to={`${cfg.base}/${o.slug}`} data-testid={`related-${o.slug}`}
                className="group flex items-center justify-between gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft transition hover:border-brand/40 hover:vc-shadow-card">
                <span className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-mint text-brand"><o.icon className="h-4 w-4" /></span>
                  <span className="text-sm font-semibold text-[hsl(var(--vc-ink))]">{o.title}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-brand transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-16">
          <div className="relative overflow-hidden rounded-3xl bg-brand px-6 py-10 text-center text-white vc-shadow-card sm:px-10 sm:py-12">
            <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Need medical support in Vietnam?</h2>
            <p className="mx-auto mt-3 max-w-xl text-base text-white/85">
              Tell us what you need and where you are. Doctors Vietnam can help coordinate an appropriate next step.
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <LeadDialog
                title={title}
                defaultType={cfg.type}
                presetService={title}
                source={`service-${service.slug}-final`}
                trigger={
                  <Button className="bg-white py-5 font-bold text-brand hover:bg-white/90" data-testid="final-get-support-btn">
                    Get Medical Support
                  </Button>
                }
              />
              <Button asChild variant="outline" className="border-2 border-white/40 bg-transparent py-5 font-bold text-white hover:bg-white/10" data-testid="final-whatsapp-btn">
                <a href={waLink(`Hello Doctors Vietnam, I need help with ${title}.`)} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
